// 中身が持つ値（2026-10-23 に state.ts の S・store.ts から分けた）: キャラのデータ・カテゴリ・カテゴリごとの今の状態・名前・柄、
// 描く先（キャラの SVG と、レイヤーごとの g）。新しい作る画面もクラシック版も、キャラはここだけに持つ（画面の値は state.ts の S）
import type { Category, CategoryData, PartState, PartsCache, Part } from '../types.js';

// 柄の1つ（pattern.ts。g の id も柄の番号）
export interface PatternItem { id: string; g: Element }

/** 何の操作で描き直したか（差しこみ口と知らせに渡す。クラシック版はこれで動きの長さを決める。新しい画面は使わない） */
export type Why = 'set' | 'load' | 'part' | 'move' | 'drag' | 'reset' | 'zoom' | 'rev' | 'rotate' | 'opacity' | 'color' | 'pattern' | 'random' | 'undo';
/** レイヤーの g に付ける位置・倍率・回転（charat.draw.js の layerTransform の引数。倍率は画面の拡大と反転の符号を掛けたもの） */
export interface LayerPlace { x: number; y: number; sx: number; sy: number; rot: number; c: { cx: number; cy: number } | null }
/** 動きの手がかり（クラシック版の操作が差しこみ口に渡す。prev は動かす前の位置、delay・duration は前のエンジンの関数の引数と同じ） */
export interface MotionInfo { prev?: LayerPlace; delay?: number; duration?: number }
/** 描き方の差しこみ口（無ければ、その場で属性を付ける） */
export interface CoreHooks {
  /** レイヤーの g に位置・反転・回転を付ける（クラシック版は d3 の動きで付ける） */
  layerTransform?: (g: SVGGElement, p: LayerPlace, why: Why, cat: Category, layer: string, info: MotionInfo) => void;
  /** パーツの g に拡大を付ける（value が null なら外す。scale はその倍率） */
  zoomTransform?: (node: Element, value: string | null, why: Why, cat: Category, scale: number, info: MotionInfo) => void;
}

/** クラシック版の柄（attach の pattern。段階 B4、2026-10-24〜）: 柄の一覧・defs・描き方はエンジンのもの（chibi.patternList・SVG_SAVE_DEF・PATTERN_PARTS_COLOR など） */
export interface ClassicPattern {
  /** 柄の g の一覧（chibi.patternList。g の id が柄の番号、同じ番号が2つあれば前のもの。柄の無いメーカー（MODE_PATTERN: false）は null） */
  list: Element[] | null;
  /** 柄の <pattern> を置く defs（背景のカテゴリは背景の SVG の defs） */
  defs: (cat: Category) => Element | null;
  partsColor: boolean;
  revParts: boolean;
  /** 色の入れかえの段の数（chibi.MODE_COLREV。0 なし → 1 → … → これの次で 0 に戻る） */
  revMax: number;
  /** キャラ全体のカテゴリの先頭レイヤー名（chibi.PARTS_CHARA。柄は付けない） */
  charaLayer: string;
  /** 柄と色の入れかえはエンジンが描く（第1世代の柄。mono の画面ファイル gen1-pattern.js が柄の作りを替えている。中身は色まで描き、柄の値はレイヤーの属性から写す） */
  engineDraws?: boolean;
}

export interface CoreState {
  /** getcategory.php の中身 */
  data: CategoryData;
  /** NAME → { SVG: 文字列 } */
  partsCache: PartsCache;
  /** カテゴリ定義（data.CATEGORY） */
  cats: Category[];
  /** カテゴリの一覧の絵（SWITCH）の SVG */
  switchRoot: Element | null;
  /** カテゴリのキー → 保存データの1行と同じ項目 */
  state: Record<string, PartState>;
  /** [名前, 名前の色] */
  name: [string, string];
  /** キャラの SVG（柄の defs・うすさの指定を置く。描く先のレイヤーの g は layerG） */
  svg: SVGSVGElement;
  /** 柄（[{ id: '001', g: 柄の g }]。g の id も '001' に。エンジンの chibi.patternList と同じ） */
  patterns: PatternItem[] | null;
  patternsLoading: Promise<PatternItem[]> | null;
  /** 画面の拡大（画像の座標 500 → 描く先の座標）。新しい画面は 1（viewBox で合わせる）。クラシック版はレイヤーの transform に入れる（chibi.scMake の倍率） */
  scale: number;
  hooks: CoreHooks;
  /** クラシック版の柄（新しい画面は null。pattern.ts の柄の一覧・決め方を使う） */
  classicPattern: ClassicPattern | null;
}

// data・svg は起動（loadData・attachLayers）で入れてから使う
export const C: CoreState = {
  data: null!,
  partsCache: {},
  cats: [],
  switchRoot: null,
  state: {},
  name: ['', '#333333'],
  svg: null!,
  patterns: null,
  patternsLoading: null,
  scale: 1,
  hooks: {},
  classicPattern: null,
};

// ALIAS か NAME → カテゴリ定義。保存データ（画像からの読み込み・#chara= では、ほかの人が作ったものも入る）のカテゴリ名で引くので、
// __proto__ などが Object の中身に当たらないようにプロトタイプなし（2026-10-22）
export const catByKey: Record<string, Category> = Object.create(null);
export const partsLists: Record<string, Part[]> = {};      // NAME → パーツの一覧（getPartsLayerList と同じ並び）
export const layerG: Record<string, SVGGElement> = {};     // レイヤー名 → キャラの SVG の g

export function blankLine(): PartState {
  return { parts: null, col: '', from: '', to: '', rev: 0, tx: 0, ty: 0, scale: 1, lfrom: '', lto: '', rfrom: '', rto: '', ptn: '', prev: 0, ptnmode: 0, rotate: 0, opacity: 1 };
}
